import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { EmptyState } from "@/components/common";
import { PRESTATION_LIST } from "@/lib/helpers";
import { toast } from "sonner";
import * as Icons from "lucide-react";

const GENERAL_LABELS = {
  tva_pct: "TVA (paramétrable, HT par défaut)",
  frais_generaux_pct: "Frais généraux",
  provision_aleas_pct: "Provision pour aléas",
  objectif_marge_pct: "Objectif de marge (taux de marque sur PV)",
};
const COUT_LABELS = {
  main_oeuvre_horaire: "Coût horaire de main-d'œuvre",
  vehicule_km: "Véhicule (au km)",
  vehicule_jour: "Véhicule (par jour)",
  deplacement_forfait: "Déplacement (forfait)",
  materiel_location_jour: "Matériel / location (par jour)",
  consommables: "Consommables",
  evacuation_dechets: "Évacuation des déchets",
};

const today = () => new Date().toISOString().slice(0, 10);

function DemoBadge() {
  return <span className="rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 text-[10px] font-medium text-amber-700">DÉMO</span>;
}

function ParamRow({ p, onChange }) {
  const set = (field) => (e) => {
    const val = e.target.value;
    if (field === "value") {
      onChange({ ...p, value: val, is_demo: false, date: today(), source: p.is_demo ? "Saisie administrateur" : p.source });
    } else {
      onChange({ ...p, [field]: val });
    }
  };
  return (
    <div className="grid grid-cols-12 items-center gap-2 rounded-lg border border-slate-100 px-2 py-2">
      <div className="col-span-3">
        <Input value={p.value ?? ""} onChange={set("value")} inputMode="decimal" className="font-mono-data h-9" data-testid={`param-value-${p._key}`} />
      </div>
      <div className="col-span-2">
        <Input value={p.unit ?? ""} onChange={set("unit")} className="h-9 text-xs" placeholder="unité" data-testid={`param-unit-${p._key}`} />
      </div>
      <div className="col-span-4">
        <Input value={p.source ?? ""} onChange={set("source")} className="h-9 text-xs" placeholder="provenance" data-testid={`param-source-${p._key}`} />
      </div>
      <div className="col-span-2 text-[11px] text-slate-400">{p.date}</div>
      <div className="col-span-1 text-right">{p.is_demo && <DemoBadge />}</div>
    </div>
  );
}

function GroupHeader() {
  return (
    <div className="grid grid-cols-12 gap-2 px-2 text-[10px] font-semibold uppercase tracking-wide text-slate-400">
      <div className="col-span-3">Valeur</div>
      <div className="col-span-2">Unité</div>
      <div className="col-span-4">Provenance</div>
      <div className="col-span-2">Date</div>
      <div className="col-span-1" />
    </div>
  );
}

export default function Pricing() {
  const { hasPerm } = useAuth();
  const canCosts = hasPerm("costs.view");
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => { if (canCosts) api.get("/pricing/settings").then((r) => setS(r.data)).catch((e) => toast.error(apiError(e))); }, [canCosts]);

  if (!canCosts) return <EmptyState icon="Lock" title="Accès réservé" testId="pricing-forbidden">Les paramètres de chiffrage sont confidentiels.</EmptyState>;
  if (!s) return <div className="flex h-[50vh] items-center justify-center text-slate-400"><Icons.Loader2 className="mr-2 h-5 w-5 animate-spin" />Chargement…</div>;

  const setParam = (group, key) => (val) => setS((prev) => ({ ...prev, [group]: { ...prev[group], [key]: val } }));

  const anyDemo = ["general", "couts", "tarifs_vente", "rendements"].some((g) => Object.values(s[g] || {}).some((p) => p?.is_demo));

  const save = async () => {
    setSaving(true);
    try {
      const clean = (grp) => Object.fromEntries(Object.entries(grp).map(([k, p]) => {
        const { _key, ...rest } = p;
        return [k, { ...rest, value: rest.value === "" || rest.value === null ? null : Number(String(rest.value).replace(",", ".")) }];
      }));
      const payload = {
        general: clean(s.general), couts: clean(s.couts), tarifs_vente: clean(s.tarifs_vente),
        rendements: clean(s.rendements), is_demo: anyDemo,
      };
      const { data } = await api.put("/pricing/settings", payload);
      setS(data);
      toast.success("Paramètres de chiffrage enregistrés");
    } catch (e) { toast.error(apiError(e)); }
    finally { setSaving(false); }
  };

  const withKey = (grp, prefix) => Object.fromEntries(Object.entries(grp).map(([k, p]) => [k, { ...p, _key: `${prefix}-${k}` }]));
  const gen = withKey(s.general, "gen");
  const couts = withKey(s.couts, "cout");
  const tarifs = withKey(s.tarifs_vente, "tar");
  const rend = withKey(s.rendements, "rend");

  return (
    <div className="space-y-5" data-testid="pricing-page">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl font-bold text-slate-900 lg:text-3xl">Paramètres de chiffrage</h1>
          <p className="text-sm text-slate-500">Espace privé administrateur. Utilisé par l'assistant « Préparer le chiffrage » (à venir).</p>
        </div>
        <Button onClick={save} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700" data-testid="pricing-save-btn">
          {saving ? <Icons.Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Icons.Save className="mr-1.5 h-4 w-4" />}Enregistrer
        </Button>
      </div>

      {anyDemo && (
        <div className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800" data-testid="pricing-demo-banner">
          <Icons.FlaskConical className="mt-0.5 h-4 w-4 shrink-0" />
          <div>Certaines valeurs sont des <b>données de démonstration</b> (badge DÉMO) — elles ne représentent pas vos vrais coûts. Modifiez une valeur pour la marquer comme la vôtre.</div>
        </div>
      )}

      <Card className="p-5">
        <h2 className="mb-1 font-display text-lg font-semibold text-slate-800">Général</h2>
        <p className="mb-3 text-xs text-slate-500">Taux de marque ≠ majoration sur coût. Frais généraux et provision s'appliquent sur les coûts directs (assiette).</p>
        <GroupHeader />
        <div className="mt-1 space-y-1.5">
          {Object.entries(gen).map(([k, p]) => (
            <div key={k}><Label className="mb-1 block text-xs text-slate-600">{GENERAL_LABELS[k] || k}</Label>
              <ParamRow p={p} onChange={setParam("general", k)} /></div>
          ))}
        </div>
      </Card>

      <Card className="p-5">
        <h2 className="mb-3 font-display text-lg font-semibold text-slate-800">Coûts</h2>
        <GroupHeader />
        <div className="mt-1 space-y-1.5">
          {Object.entries(couts).map(([k, p]) => (
            <div key={k}><Label className="mb-1 block text-xs text-slate-600">{COUT_LABELS[k] || k}</Label>
              <ParamRow p={p} onChange={setParam("couts", k)} /></div>
          ))}
        </div>
      </Card>

      <Card className="p-5">
        <h2 className="mb-3 font-display text-lg font-semibold text-slate-800">Tarifs de vente (m² / ml / U)</h2>
        <GroupHeader />
        <div className="mt-1 space-y-1.5">
          {PRESTATION_LIST.map((pr) => tarifs[pr.id] && (
            <div key={pr.id}><Label className="mb-1 block text-xs text-slate-600">{pr.name}</Label>
              <ParamRow p={tarifs[pr.id]} onChange={setParam("tarifs_vente", pr.id)} /></div>
          ))}
        </div>
      </Card>

      <Card className="p-5">
        <h2 className="mb-1 font-display text-lg font-semibold text-slate-800">Rendements de référence</h2>
        <p className="mb-3 text-xs text-slate-500">Précisez si le rendement est exprimé <b>par personne</b> ou <b>par équipe</b> (évite les erreurs de calcul).</p>
        <GroupHeader />
        <div className="mt-1 space-y-1.5">
          {PRESTATION_LIST.map((pr) => rend[pr.id] && (
            <div key={pr.id}>
              <div className="mb-1 flex items-center justify-between">
                <Label className="text-xs text-slate-600">{pr.name}</Label>
                <Select value={rend[pr.id].basis || "personne"} onValueChange={(v) => setParam("rendements", pr.id)({ ...rend[pr.id], basis: v })}>
                  <SelectTrigger className="h-7 w-40 text-xs" data-testid={`rend-basis-${pr.id}`}><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="personne">Par personne</SelectItem>
                    <SelectItem value="equipe">Par équipe</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <ParamRow p={rend[pr.id]} onChange={setParam("rendements", pr.id)} />
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
}
